import { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { Search } from './../../../../Common/Tables/Search'
//import { InstructorBase } from './../../../../Layouts/Instructor/InstructorBase'
import { InstructorBase } from './../../../../Layouts/Instructor/V2/InstructorBase'
import { Details } from './../../../../Common/Modals/Details'
import { Confirm } from './../../../../Common/Modals/Confirm'
import { GetZtudentsOfGroup, AcceptZtudent as BLAcceptZtudent, RejectZtudent as BLRejectZtudent, DeleteZtudentOfGroup as BLDeleteZtudentOfGroup, GetCurrentGroup, HideZtudent as BLHideZtudent, ShowZtudent as BLShowZtudent, GetZtudentGroups, RenameGroup as BLRenameGroup } from './../../../../../utilities/BL/Instructor/Groups'
import { SaveGroupToShow } from './../../../../../utilities/BL/Navigation'
import { ResetZtudiantePassword as BLResetZtudiantePassword } from './../../../../../utilities/BL/Instructor/Ztudents'

import { Loading } from './../../../../Common/Loading'

import { LogErrors } from './../../../../../utilities/LogErrors'
import { AccessCode } from './../../../../Common/Modals/AccessCode'
import { ValidateSession } from './../../../../../utilities/BL/Sessions'
import { useFloatMessage, MESSAGE_TYPE } from './../../../../Providers/FloatMessage'
import { HELP_TYPE } from './../../../../../utilities/EL/HelpType'
import { WEBPAGES } from '../../../../../utilities/EL/WebPages'
import { ROLES } from './../../../../../utilities/EL/Roles'


// -----------------------------------------------------------------------------
// Componente de detalle de un grupo.
// Muestra:
//  - Nombre del grupo y botón para ver código de acceso
//  - Listado de Ztudiantes visibles y ocultos
//  - Acciones individuales y masivas (aceptar, rechazar, ocultar, mostrar, eliminar, reset password)
//  - NUEVO: Botón para cambiar el nombre del grupo (editar + confirmar)
// -----------------------------------------------------------------------------

const Group = () => {
  const navigate = useNavigate()
  const { showMessage } = useFloatMessage()

  // ---------------------------------------------------------------------------
  // Estado general de la página
  // ---------------------------------------------------------------------------
  const [isLoading, setIsLoading] = useState(true)

  // Modales de detalles y confirmación para Ztudiantes
  const [showDetailsModal, setShowDetailsModal] = useState(false)
  const [showConfirmationModal, setShowConfirmationModal] = useState(false)
  const [actionTypeOfModal, setActionTypeOfModal] = useState(null)

  const [ztudentDetails, setZtudentDetails] = useState(null)
  const [ztudentConfirmDetails, setZtudentConfirmDetails] = useState(null)

  // Resumen de acciones masivas sobre Ztudiantes
  const [massiveSummary, setMassiveSummary] = useState(null)
  const [showMassiveSummaryModal, setShowMassiveSummaryModal] = useState(false)

  // Modal para ver código de acceso del grupo
  const [showAccessCodeModal, setShowAccessCodeModal] = useState(false)

  // Navegación dentro de modal de detalles (prev/next Ztudiante)
  const [showPreviousStepButton, setShowPreviousStepButton] = useState(false)
  const [showNextStepButton, setShowNextStepButton] = useState(false)

  // Información del grupo actual
  const [currentGroup, setCurrentGroup] = useState(null)

  // Listas filtradas de Ztudiantes
  const [visibleRows, setVisibleRows] = useState([])
  const [hiddenRows, setHiddenRows] = useState([])

  // Texto de búsqueda
  const [visibleSearchText, setVisibleSearchText] = useState('')
  const [hiddenSearchText, setHiddenSearchText] = useState('')

  // Vista actual en detalles (para navegar entre visibles/ocultos)
  const [currentTableView, setCurrentTableView] = useState('visible') // 'visible' o 'hidden'

  // Índice del Ztudiante actual en detalles
  const [ztudentIndex, setZtudentIndex] = useState(null)

  // Código de acceso del grupo
  const [accessCode, setAccessCode] = useState(null)

  // ---------------------------------------------------------------------------
  // Definición de tipos de acción sobre Ztudiantes
  // ---------------------------------------------------------------------------
  const actions = {
    ACCEPT: 'ACCEPT',
    REJECT: 'REJECT',
    HIDE: 'HIDE',
    SHOW: 'SHOW',
    DELETE: 'DELETE',
    RESET: 'RESET'
  }

  // Refs para mantener listas actualizadas dentro de callbacks asíncronos
  const visibleRowsRef = useRef([])
  const hiddenRowsRef = useRef([])

  useEffect(() => {
    visibleRowsRef.current = visibleRows
  }, [visibleRows])

  useEffect(() => {
    hiddenRowsRef.current = hiddenRows
  }, [hiddenRows])

  // ---------------------------------------------------------------------------
  // Listado completo (base) y refs
  // ---------------------------------------------------------------------------
  const [allRows, setAllRows] = useState([])
  const allRowsRef = useRef([])

  useEffect(() => {
    allRowsRef.current = allRows
  }, [allRows])

  // ---------------------------------------------------------------------------
  // Búsquedas (visibles / ocultos)
  // ---------------------------------------------------------------------------
  const handleVisibleSearch = async (value) => {
    try {
      setVisibleSearchText(value)
      const text = value.toLowerCase()

      const filtered = allRowsRef.current.filter(
        (row) =>
          row.isVisible &&
          Object.values(row).some((val) => String(val).toLowerCase().includes(text))
      )
      setVisibleRows(filtered)
    } catch (error) {
      await LogErrors('handleVisibleSearch - Group', error)
    }
  }

  const handleHiddenSearch = async (value) => {
    try {
      setHiddenSearchText(value)
      const text = value.toLowerCase()

      const filtered = allRowsRef.current.filter(
        (row) =>
          !row.isVisible &&
          Object.values(row).some((val) => String(val).toLowerCase().includes(text))
      )
      setHiddenRows(filtered)
    } catch (error) {
      await LogErrors('handleHiddenSearch - Group', error)
    }
  }

  // Total de Ztudiantes ocultos (sin filtro)
  const hiddenTotal = useMemo(
    () => allRows.filter((z) => !z.isVisible).length,
    [allRows]
  )

  // ---------------------------------------------------------------------------
  // Lógica de tablas (selección, ordenamiento) - VISIBLES
  // ---------------------------------------------------------------------------
  const [selectedVisibleIds, setSelectedVisibleIds] = useState([])
  const [sortVisibleConfig, setSortVisibleConfig] = useState({
    key: null,
    direction: 'asc'
  })

  const isVisibleSelected = (idZtudiante) => selectedVisibleIds.includes(idZtudiante)

  const toggleVisibleSelect = (idZtudiante) => {
    setSelectedVisibleIds((prev) =>
      prev.includes(idZtudiante)
        ? prev.filter((i) => i !== idZtudiante)
        : [...prev, idZtudiante]
    )
  }

  const handleSortVisible = (key) => {
    setSortVisibleConfig((prev) => {
      if (prev.key === key) {
        return { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' }
      }
      return { key, direction: 'asc' }
    })
  }

  const sortedVisibleRows = useMemo(() => {
    if (!sortVisibleConfig.key) return visibleRows

    return [...visibleRows].sort((a, b) => {
      const aVal = a[sortVisibleConfig.key]
      const bVal = b[sortVisibleConfig.key]

      return typeof aVal === 'string'
        ? sortVisibleConfig.direction === 'asc'
          ? aVal.localeCompare(bVal)
          : bVal.localeCompare(aVal)
        : sortVisibleConfig.direction === 'asc'
        ? aVal - bVal
        : bVal - aVal
    })
  }, [visibleRows, sortVisibleConfig])

  // ---------------------------------------------------------------------------
  // Lógica de tablas (selección, ordenamiento) - OCULTOS
  // ---------------------------------------------------------------------------
  const [selectedHiddenIds, setSelectedHiddenIds] = useState([])
  const [sortHiddenConfig, setSortHiddenConfig] = useState({
    key: null,
    direction: 'asc'
  })

  const isHiddenSelected = (idZtudiante) => selectedHiddenIds.includes(idZtudiante)

  const toggleHiddenSelect = (idZtudiante) => {
    setSelectedHiddenIds((prev) =>
      prev.includes(idZtudiante)
        ? prev.filter((i) => i !== idZtudiante)
        : [...prev, idZtudiante]
    )
  }

  const handleSortHidden = (key) => {
    setSortHiddenConfig((prev) => {
      if (prev.key === key) {
        return { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' }
      }
      return { key, direction: 'asc' }
    })
  }

  const sortedHiddenRows = useMemo(() => {
    if (!sortHiddenConfig.key) return hiddenRows

    return [...hiddenRows].sort((a, b) => {
      const aVal = a[sortHiddenConfig.key]
      const bVal = b[sortHiddenConfig.key]

      return typeof aVal === 'string'
        ? sortHiddenConfig.direction === 'asc'
          ? aVal.localeCompare(bVal)
          : bVal.localeCompare(aVal)
        : sortHiddenConfig.direction === 'asc'
        ? aVal - bVal
        : bVal - aVal
    })
  }, [hiddenRows, sortHiddenConfig])

  // ---------------------------------------------------------------------------
  // Helpers comunes a acciones masivas / individuales sobre Ztudiantes
  // ---------------------------------------------------------------------------

  /**
   * Limpia las selecciones de Ztudiantes (visibles y ocultos)
   */
  const ClearSelections = async () => {
    try {
      setSelectedVisibleIds([])
      setSelectedHiddenIds([])
    } catch (error) {
      await LogErrors('ClearSelections - Group', error)
    }
  }

  /**
   * Fusiona listas de Ztudiantes visibles y ocultos en una sola,
   * tomando currentRows como base y sobreescribiendo con updatedVisible/updatedHidden.
   */
  const MergeUpdatedRows = async (currentRows, updatedVisible, updatedHidden) => {
    try {
      const map = new Map(currentRows.map((z) => [z.idZtudiante, z]))
      updatedVisible?.forEach((z) => map.set(z.idZtudiante, z))
      updatedHidden?.forEach((z) => map.set(z.idZtudiante, z))
      return Array.from(map.values())
    } catch (error) {
      await LogErrors('MergeUpdatedRows - Group', error)
    }
  }

  /**
   * Ejecuta una acción masiva sobre un conjunto de Ztudiantes.
   * Recibe:
   *  - rows: arreglo de Ztudiantes a procesar
   *  - logicFn: función BL que ejecuta la acción para cada Ztudiante
   * Retorna un resumen con successful / wrongs / omitteds.
   */
  const PerformMassiveAction = async (rows, logicFn) => {
    try {
      const resumen = { successful: [], wrongs: [], omitteds: [] }
      let currentRows = [...allRowsRef.current]

      for (const ztudent of rows) {
        const stillExists = currentRows.find(
          (z) => z.idZtudiante === ztudent.idZtudiante
        )
        if (!stillExists) {
          resumen.omitteds.push({
            ...ztudent,
            reason: 'Ya no existe en el listado'
          })
          continue
        }

        try {
          const response = await logicFn(ztudent, currentRows)

          if (response.success) {
            resumen.successful.push(ztudent)

            // Detectar estructura de respuesta
            if (response.data?.updatedVisible && response.data?.updatedHidden) {
              // HideZtudent / ShowZtudent
              currentRows = await MergeUpdatedRows(
                currentRows,
                response.data.updatedVisible,
                response.data.updatedHidden
              )
            } else if (Array.isArray(response.data)) {
              // Accept / Reject / Delete
              currentRows = response.data
            } else {
              console.warn(
                'Estructura desconocida en respuesta de acción masiva:',
                response
              )
            }
          } else {
            resumen.wrongs.push({
              ...ztudent,
              error: response.message ?? 'Error desconocido'
            })
          }
        } catch (error) {
          resumen.wrongs.push({
            ...ztudent,
            error: error.message
          })
        }
      }

      setAllRows(currentRows)
      setVisibleRows(currentRows.filter((z) => z.isVisible))
      setHiddenRows(currentRows.filter((z) => !z.isVisible))

      return { success: true, data: resumen }
    } catch (error) {
      await LogErrors('PerformMassiveAction - Group', error)
    }
  }

  // ---------------------------------------------------------------------------
  // Acciones individuales sobre Ztudiantes
  // ---------------------------------------------------------------------------

  const AcceptZtudent = async () => {
    try {
      if (ztudentConfirmDetails) {
        const response = await BLAcceptZtudent(
          ztudentConfirmDetails,
          allRowsRef.current
        )

        if (response.success) {
          showMessage({
            type: MESSAGE_TYPE.SUCCESS,
            text: `El Ztudiante ${ztudentConfirmDetails.name} ${ztudentConfirmDetails.lastName} (${ztudentConfirmDetails.user}) ha sido aceptado en el grupo exitosamente.`
          })

          setAllRows(response.data)
          setVisibleRows(response.data.filter((z) => z.isVisible))
          setHiddenRows(response.data.filter((z) => !z.isVisible))
        }

        await ClearSelections()
        return response
      }
    } catch (error) {
      await LogErrors('AcceptZtudent - Group', error)
    }
  }

  const AcceptMultipleZtudents = async (_actionTypeOfModal, selectedRows) => {
    try {
      const response = await PerformMassiveAction(selectedRows, BLAcceptZtudent)

      if (response.success) {
        setMassiveSummary(response.data)
        setShowMassiveSummaryModal(true)
        setActionTypeOfModal(_actionTypeOfModal)
        await ClearSelections()
      }
    } catch (error) {
      await LogErrors('AcceptMultipleZtudents - Group', error)
    }
  }

  const RejectZtudent = async () => {
    try {
      if (ztudentConfirmDetails) {
        const response = await BLRejectZtudent(
          ztudentConfirmDetails,
          allRowsRef.current
        )

        if (response.success) {
          showMessage({
            type: MESSAGE_TYPE.SUCCESS,
            text: `El Ztudiante ${ztudentConfirmDetails.name} ${ztudentConfirmDetails.lastName} (${ztudentConfirmDetails.user}) ha sido rechazado del grupo exitosamente.`
          })

          setAllRows(response.data)
          setVisibleRows(response.data.filter((z) => z.isVisible))
          setHiddenRows(response.data.filter((z) => !z.isVisible))
        }

        await ClearSelections()
        return response
      }
    } catch (error) {
      await LogErrors('RejectZtudent - Group', error)
    }
  }

  const RejectMultipleZtudents = async (_actionTypeOfModal, selectedRows) => {
    try {
      const response = await PerformMassiveAction(selectedRows, BLRejectZtudent)

      if (response.success) {
        setMassiveSummary(response.data)
        setShowMassiveSummaryModal(true)
        setActionTypeOfModal(_actionTypeOfModal)
        await ClearSelections()
      }
    } catch (error) {
      await LogErrors('RejectMultipleZtudents - Group', error)
    }
  }

  const HideZtudent = async () => {
    try {
      if (ztudentConfirmDetails) {
        const response = await BLHideZtudent(
          ztudentConfirmDetails,
          allRowsRef.current
        )

        if (response.success) {
          showMessage({
            type: MESSAGE_TYPE.SUCCESS,
            text: `El Ztudiante ${ztudentConfirmDetails.name} ${ztudentConfirmDetails.lastName} (${ztudentConfirmDetails.user}) ha sido ocultado exitosamente.`
          })

          const updatedRows = await MergeUpdatedRows(
            allRowsRef.current,
            response.data.updatedVisible,
            response.data.updatedHidden
          )

          setAllRows(updatedRows)
          setVisibleRows(updatedRows.filter((z) => z.isVisible))
          setHiddenRows(updatedRows.filter((z) => !z.isVisible))
        }

        await ClearSelections()
        return response
      }
    } catch (error) {
      await LogErrors('HideZtudent - Group', error)
    }
  }

  const HideMultipleZtudents = async (_actionTypeOfModal, selectedRows) => {
    try {
      const response = await PerformMassiveAction(selectedRows, BLHideZtudent)

      if (response.success) {
        setMassiveSummary(response.data)
        setShowMassiveSummaryModal(true)
        setActionTypeOfModal(_actionTypeOfModal)
        await ClearSelections()
      }
    } catch (error) {
      await LogErrors('HideMultipleZtudents - Group', error)
    }
  }

  const ShowZtudent = async () => {
    try {
      if (ztudentConfirmDetails) {
        const response = await BLShowZtudent(
          ztudentConfirmDetails,
          hiddenRows,
          allRowsRef.current
        )

        if (response.success) {
          showMessage({
            type: MESSAGE_TYPE.SUCCESS,
            text: `El Ztudiante ${ztudentConfirmDetails.name} ${ztudentConfirmDetails.lastName} (${ztudentConfirmDetails.user}) ha sido mostrado exitosamente.`
          })

          const updatedRows = await MergeUpdatedRows(
            allRowsRef.current,
            response.data.updatedVisible,
            response.data.updatedHidden
          )

          setAllRows(updatedRows)
          setVisibleRows(updatedRows.filter((z) => z.isVisible))
          setHiddenRows(updatedRows.filter((z) => !z.isVisible))
        }

        await ClearSelections()
        return response
      }
    } catch (error) {
      await LogErrors('ShowZtudent - Group', error)
    }
  }

  const ShowMultipleZtudents = async (_actionTypeOfModal, selectedRows) => {
    try {
      const response = await PerformMassiveAction(selectedRows, (ztudent, rows) =>
        BLShowZtudent(ztudent, rows, allRowsRef.current)
      )

      if (response.success) {
        setMassiveSummary(response.data)
        setShowMassiveSummaryModal(true)
        setActionTypeOfModal(_actionTypeOfModal)
        await ClearSelections()
      }
    } catch (error) {
      await LogErrors('ShowMultipleZtudents - Group', error)
    }
  }

  const DeleteZtudentOfGroup = async () => {
    try {
      if (ztudentConfirmDetails) {
        const response = await BLDeleteZtudentOfGroup(
          ztudentConfirmDetails,
          allRowsRef.current
        )

        if (response.success) {
          showMessage({
            type: MESSAGE_TYPE.SUCCESS,
            text: `El Ztudiante ${ztudentConfirmDetails.name} ${ztudentConfirmDetails.lastName} (${ztudentConfirmDetails.user}) ha sido eliminado del grupo exitosamente.`
          })

          setAllRows(response.data)
          setVisibleRows(response.data.filter((z) => z.isVisible))
          setHiddenRows(response.data.filter((z) => !z.isVisible))
        }

        await ClearSelections()
        return response
      }
    } catch (error) {
      await LogErrors('DeleteZtudentOfGroup - Group', error)
    }
  }

  const DeleteMultipleZtudentsOfGroup = async (_actionTypeOfModal, selectedRows) => {
    try {
      const response = await PerformMassiveAction(
        selectedRows,
        BLDeleteZtudentOfGroup
      )

      if (response.success) {
        setMassiveSummary(response.data)
        setShowMassiveSummaryModal(true)
        setActionTypeOfModal(_actionTypeOfModal)
        await ClearSelections()
      }
    } catch (error) {
      await LogErrors('DeleteMultipleZtudentsOfGroup - Group', error)
    }
  }

  const ResetZtudiantePassword = async () => {
    try {
      if (ztudentConfirmDetails) {
        const response = await BLResetZtudiantePassword(ztudentConfirmDetails)

        if (response.success) {
          showMessage({
            type: MESSAGE_TYPE.SUCCESS,
            text: `Se ha reiniciado exitosamente la contraseña del Ztudiante ${ztudentConfirmDetails.name} ${ztudentConfirmDetails.lastName} (${ztudentConfirmDetails.user}).`
          })
        }
        return response
      }
    } catch (error) {
      await LogErrors('ResetZtudiantePassword - Group', error)
    }
  }

  // ---------------------------------------------------------------------------
  // Modal de confirmación genérico para acciones sobre Ztudiantes
  // ---------------------------------------------------------------------------

  const ShowConfirmModal = async (actionType, ztudent) => {
    try {
      if (actionType && ztudent) {
        if (
          actionType === actions.ACCEPT ||
          actionType === actions.REJECT ||
          actionType === actions.HIDE ||
          actionType === actions.DELETE ||
          actionType === actions.SHOW ||
          actionType === actions.RESET
        ) {
          setActionTypeOfModal(actionType)
          setZtudentConfirmDetails(ztudent)
          setShowConfirmationModal(true)
        } else {
          setActionTypeOfModal(null)
          setZtudentConfirmDetails(null)
          setShowConfirmationModal(false)
          return
        }
      }
    } catch (error) {
      await LogErrors('ShowConfirmModal - Group', error)
    }
  }

  // ---------------------------------------------------------------------------
  // Modal de detalles de Ztudiante (con navegación previa / siguiente)
  // ---------------------------------------------------------------------------

  const ShowOrHideStepsButtons = async (index, length) => {
    try {
      setShowPreviousStepButton(index > 0)
      setShowNextStepButton(index < length - 1)
    } catch (error) {
      await LogErrors('ShowOrHideStepsButtons - Group', error)
    }
  }

  const ShowZtudentDetails = async (ztudent) => {
    try {
      const baseList = ztudent.isVisible
        ? visibleRowsRef.current
        : hiddenRowsRef.current
      setCurrentTableView(ztudent.isVisible ? 'visible' : 'hidden')

      const tmpZtudentDetails = baseList.find(
        (row) => row.idZtudiante === ztudent.idZtudiante
      )
      if (!tmpZtudentDetails) return

      const response = await GetZtudentGroups(ztudent)
      const finalZtudent = response.success
        ? { ...tmpZtudentDetails, groups: response.data }
        : tmpZtudentDetails

      setZtudentDetails(finalZtudent)

      const index = baseList.findIndex(
        (row) => row.idZtudiante === ztudent.idZtudiante
      )
      setZtudentIndex(index)
      await ShowOrHideStepsButtons(index, baseList.length)

      setShowDetailsModal(true)
    } catch (error) {
      await LogErrors('ShowZtudentDetails - Group', error)
    }
  }

  const PreviousStepButton = async () => {
    try {
      const list =
        currentTableView === 'visible'
          ? visibleRowsRef.current
          : hiddenRowsRef.current
      if (ztudentIndex <= 0) return

      const newIndex = ztudentIndex - 1
      const ztudent = list[newIndex]

      const response = await GetZtudentGroups(ztudent)
      const finalZtudent = response.success
        ? { ...ztudent, groups: response.data }
        : ztudent

      setZtudentDetails(finalZtudent)
      setZtudentIndex(newIndex)
      await ShowOrHideStepsButtons(newIndex, list.length)
    } catch (error) {
      await LogErrors('PreviousStepButton - Group', error)
    }
  }

  const NextStepButton = async () => {
    try {
      const list =
        currentTableView === 'visible'
          ? visibleRowsRef.current
          : hiddenRowsRef.current
      if (ztudentIndex === -1 || ztudentIndex + 1 >= list.length) return

      const newIndex = ztudentIndex + 1
      const ztudent = list[newIndex]

      const response = await GetZtudentGroups(ztudent)
      const finalZtudent = response.success
        ? { ...ztudent, groups: response.data }
        : ztudent

      setZtudentDetails(finalZtudent)
      setZtudentIndex(newIndex)
      await ShowOrHideStepsButtons(newIndex, list.length)
    } catch (error) {
      await LogErrors('NextStepButton - Group', error)
    }
  }

  // ---------------------------------------------------------------------------
  // Modal para código de acceso
  // ---------------------------------------------------------------------------

  const ShowAccessCode = async () => {
    try {
      setShowAccessCodeModal(true)
    } catch (error) {
      await LogErrors('ShowAccessCode - Group', error)
    }
  }

  // ---------------------------------------------------------------------------
  // Helpers para detectar tipos de Ztudiantes en selección masiva
  // ---------------------------------------------------------------------------

  const hasUnacceptedZtudents = selectedVisibleIds.some((id) => {
    try {
      const z = visibleRows.find((row) => row.idZtudiante === id)
      return z && z.isAccepted === false
    } catch (error) {}
  })

  const hasAcceptedZtudents = selectedVisibleIds.some((id) => {
    try {
      const z = visibleRows.find((row) => row.idZtudiante === id)
      return z && z.isAccepted === true
    } catch (error) {}
  })

  // ---------------------------------------------------------------------------
  // Carga inicial de grupo y Ztudiantes
  // ---------------------------------------------------------------------------

  const LoadPage = async () => {
    try {
      const responseGroup = await GetCurrentGroup()

      if (responseGroup.success) {
        setCurrentGroup(responseGroup.data)

        const response = await GetZtudentsOfGroup(responseGroup.data)

        if (response.success) {
          setAllRows(response?.data?.ztudiantes)
          setVisibleRows(response.data.ztudiantes.filter((z) => z.isVisible))
          setHiddenRows(response.data.ztudiantes.filter((z) => !z.isVisible))

          setAccessCode(response?.data?.accesCode)
        } else {
          console.log(response.message)
        }
      }
    } catch (error) {
      await LogErrors('LoadPage - Group', error)
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    async function Validate() {
      const tmpSession = await ValidateSession()

      if (tmpSession.success) {
        if (tmpSession.data.user.userStatus === 2) {
          await LoadPage()
        } else {
          navigate('/confirm')
        }
      } else {
        navigate(`/?logout=${process.env.REACT_APP_LOGOUT_PARAMETER}`)
      }
    }
    Validate()
  }, []) // eslint-disable-line

  // ---------------------------------------------------------------------------
  // NUEVO: Renombrar grupo (estado, modales y lógica)
  // ---------------------------------------------------------------------------

  // Modal para editar el nombre (input)
  const [showEditGroupModal, setShowEditGroupModal] = useState(false)

  // Texto del nuevo nombre que escribe el usuario
  const [editingGroupName, setEditingGroupName] = useState('')

  // Datos para el modal de confirmación de renombrado
  const [groupRenameConfirmData, setGroupRenameConfirmData] = useState(null)

  // Mostrar/ocultar modal de confirmación de renombrado
  const [showGroupRenameConfirmModal, setShowGroupRenameConfirmModal] =
    useState(false)

  /**
   * Abre el modal para editar el nombre del grupo.
   * Precarga en el input el nombre actual del grupo.
   */
  const OpenEditGroupNameModal = async () => {
    try {
      if (!currentGroup) return
      setEditingGroupName(currentGroup.name || '')
      setShowEditGroupModal(true)
    } catch (error) {
      await LogErrors('OpenEditGroupNameModal - Group', error)
    }
  }

  /**
   * Ejecuta el renombrado del grupo llamando al BLRenameGroup(idGroup, newName)
   * y actualiza el estado local currentGroup para reflejar el cambio en pantalla.
   */
  const RenameCurrentGroup = async () => {
    try {
      if (!groupRenameConfirmData || !currentGroup) return

      //const { idGroup, oldName, newName } = groupRenameConfirmData
      const { oldName, newName } = groupRenameConfirmData

      const responseGroup = await GetCurrentGroup()

      if (responseGroup.success) {
        const idGroup = responseGroup?.data?.id
        
        if (!idGroup) {
          return
        }
        
        const response = await BLRenameGroup(idGroup, newName)
        
        if (response?.success) {
          showMessage({
            type: MESSAGE_TYPE.SUCCESS,
            text: `El grupo ${oldName} ha sido renombrado a ${newName}.`
          })
  
          // Actualizamos el nombre en el estado local del grupo
          setCurrentGroup((prev) =>
            prev ? { ...prev, name: newName } : prev
          )
        } else if (response && response.message) {
          showMessage({
            type: MESSAGE_TYPE.BAD,
            text: response.message
          })
        } else {
          showMessage({
            type: MESSAGE_TYPE.BAD,
            text: 'No ha sido posible renombrar el grupo. Inténtalo nuevamente o más tarde.'
          })
        }
  
        return response
      } else {
        return
      }
    } catch (error) {
      await LogErrors('RenameCurrentGroup - Group', error)
    }
  }

  // ---------------------------------------------------------------------------
  // RENDER
  // ---------------------------------------------------------------------------

  return isLoading ? (
    <Loading />
  ) : (
    <>
      <InstructorBase urlToGo={'/instructor/groups'} idHelp={HELP_TYPE.INSTRUCTOR.GROUP} page={WEBPAGES.INSTRUCTOR.GROUP} role={ROLES.INSTRUCTOR}>
        <section className='container Instructor__Group'>
          {/* ----------------------------------------------------------------- */}
          {/* Encabezado del grupo + botón para renombrar                        */}
          {/* ----------------------------------------------------------------- */}
          <p className='Instructor__Title Instructor__Title--Center'>
            Grupo: {currentGroup?.name}
            <button
              type='button'
              className='Btn__Ztudia--PrimaryOutline Instructor__Group__EditButton'
              onClick={OpenEditGroupNameModal}
            >
              <i className='bi bi-pencil'></i>
            </button>
          </p>

          {/* <div className='Instructor__Group__HeaderButtons'>
            <button
              type='button'
              className='Btn__Ztudia--PrimaryOutline Instructor__Group__RenameButton'
              onClick={OpenEditGroupNameModal}
            >
              <i className='bi bi-pencil'></i>
            </button>
          </div> */}

          {/* ----------------------------------------------------------------- */}
          {/* Contenido principal: tablas de Ztudiantes                         */}
          {/* ----------------------------------------------------------------- */}
          {allRows && (
            <>
              {/* Totales visibles */}
              {allRows && (
                <p className='Instructor__Subtitle Instructor__Subtitle--Center'>
                  Total de Ztudiantes: {allRows.filter((z) => z.isVisible).length}
                </p>
              )}

              <button
                type='button'
                className='Btn__Ztudia--Link Instructor__AccessCodeButton'
                onClick={() => ShowAccessCode()}
              >
                Ver código Ztudia
              </button>

              {/* ===================== TABLA VISIBLES ===================== */}
              {visibleRows ? (
                <>
                  {/* 🔎 Buscador de visibles */}
                  <Search
                    onChange={handleVisibleSearch}
                    extraClassName={'Instructor__SpaceUp'}
                  />

                  <div className='container Instructor__SpaceUp Tables--Shadow'>
                    {/* Acciones masivas visibles */}
                    {selectedVisibleIds.length > 0 && (
                      <div className='Tables__ExtraButtons'>
                        {hasUnacceptedZtudents && (
                          <>
                            <button
                              className='Btn__Ztudia--PrimaryOutline Tables__ExtraButtons__Button'
                              onClick={() =>
                                AcceptMultipleZtudents(
                                  actions.ACCEPT,
                                  selectedVisibleIds.map((id) =>
                                    allRows.find((row) => row.idZtudiante === id)
                                  )
                                )
                              }
                            >
                              Aceptar {selectedVisibleIds.length} Ztudiante(s)
                            </button>
                            <button
                              className='Btn__Ztudia--PrimaryOutline Tables__ExtraButtons__Button'
                              onClick={() =>
                                RejectMultipleZtudents(
                                  actions.REJECT,
                                  selectedVisibleIds.map((id) =>
                                    allRows.find((row) => row.idZtudiante === id)
                                  )
                                )
                              }
                            >
                              Rechazar {selectedVisibleIds.length} Ztudiante(s)
                            </button>
                          </>
                        )}
                        {hasAcceptedZtudents && (
                          <>
                            <button
                              className='Btn__Ztudia--PrimaryOutline Tables__ExtraButtons__Button'
                              onClick={() =>
                                HideMultipleZtudents(
                                  actions.HIDE,
                                  selectedVisibleIds.map((id) =>
                                    allRows.find((row) => row.idZtudiante === id)
                                  )
                                )
                              }
                            >
                              Ocultar {selectedVisibleIds.length} Ztudiante(s)
                            </button>
                            <button
                              className='Btn__Ztudia--PrimaryOutline Tables__ExtraButtons__Button'
                              onClick={() =>
                                DeleteMultipleZtudentsOfGroup(
                                  actions.DELETE,
                                  selectedVisibleIds.map((id) =>
                                    allRows.find((row) => row.idZtudiante === id)
                                  )
                                )
                              }
                            >
                              Eliminar {selectedVisibleIds.length} Ztudiante(s)
                            </button>
                          </>
                        )}
                      </div>
                    )}

                    {/* Franja "X ztudiantes seleccionados" (visibles) */}
                    {selectedVisibleIds.length > 0 && (
                      <div className='Tables__Table__RegistersSelected'>
                        <button
                          className='Tables__Table__RegistersSelected__Button Tables__Table__RegistersSelected__Button--Sm'
                          onClick={() => setSelectedVisibleIds([])}
                        >
                          <span className='Tables__Table__RegistersSelected__Button__Text'>
                            X
                          </span>
                          <span className='Tables__Table__RegistersSelected__Button__Text'>
                            Cerrar
                          </span>
                        </button>

                        <div className='Tables__Table__RegistersSelected__Text'>
                          {selectedVisibleIds.length} ztudiante(s) seleccionada(s)
                        </div>

                        <button
                          className='Tables__Table__RegistersSelected__Button Tables__Table__RegistersSelected__Button--Lg'
                          onClick={() => setSelectedVisibleIds([])}
                        >
                          <span className='Tables__Table__RegistersSelected__Button__Text'>
                            X
                          </span>
                          <span className='Tables__Table__RegistersSelected__Button__Text'>
                            Cerrar
                          </span>
                        </button>
                      </div>
                    )}

                    {/* Tabla de visibles */}
                    <section className='Tables__Container'>
                      <table className='Tables__Table Instructor__Group__Visible__ContainerTable__Table'>
                        {selectedVisibleIds.length === 0 && (
                          <thead>
                            <tr className='Tables__Table__Row--Header'>
                              <td className='Tables__Table__Row__Cell Tables__Table__Header Tables__Table__InputColumn Instructor__Group__Visible__ContainerTable__Table__InputColumn'>
                                <input
                                  type='checkbox'
                                  className='Tables__Table__InputColumn__Input'
                                  onChange={(e) => {
                                    const newIds = e.target.checked
                                      ? visibleRows.map((row) => row.idZtudiante)
                                      : []
                                    setSelectedVisibleIds(newIds)
                                  }}
                                  checked={
                                    visibleRows.length > 0 &&
                                    visibleRows.every((row) =>
                                      selectedVisibleIds.includes(row.idZtudiante)
                                    )
                                  }
                                />
                              </td>
                              <td
                                className='Tables__Table__Row__Cell Tables__Table__Header Tables__Table__TextColumn Instructor__Group__Visible__ContainerTable__Table__TextColumn'
                                onClick={() => handleSortVisible('name')}
                              >
                                Nombre(s){' '}
                                {sortVisibleConfig.key === 'name' && (
                                  <span>
                                    {sortVisibleConfig.direction === 'asc' ? ' ▲' : ' ▼'}
                                  </span>
                                )}
                              </td>
                              <td
                                className='Tables__Table__Row__Cell Tables__Table__Header Tables__Table__TextColumn Instructor__Group__Visible__ContainerTable__Table__TextColumn'
                                onClick={() => handleSortVisible('lastName')}
                              >
                                Apellido(s){' '}
                                {sortVisibleConfig.key === 'lastName' && (
                                  <span>
                                    {sortVisibleConfig.direction === 'asc' ? ' ▲' : ' ▼'}
                                  </span>
                                )}
                              </td>
                              <td
                                className='Tables__Table__Row__Cell Tables__Table__Header Tables__Table__TextColumn Instructor__Group__Visible__ContainerTable__Table__TextColumn'
                                onClick={() => handleSortVisible('user')}
                              >
                                Usuario{' '}
                                {sortVisibleConfig.key === 'user' && (
                                  <span>
                                    {sortVisibleConfig.direction === 'asc' ? ' ▲' : ' ▼'}
                                  </span>
                                )}
                              </td>
                              <td className='Tables__Table__Row__Cell Tables__Table__Header Tables__Table__ActionButtonsColumn Instructor__Group__Visible__ContainerTable__Table__ActionButtonsColumn'></td>
                              <td className='Tables__Table__Row__Cell Tables__Table__Header Tables__Table__ActionButtonsColumn Instructor__Group__Visible__ContainerTable__Table__ActionButtonsColumn'></td>
                              <td className='Tables__Table__Row__Cell Tables__Table__Header Tables__Table__ActionButtonsColumn Instructor__Group__Visible__ContainerTable__Table__ActionButtonsColumn'></td>
                              <td className='Tables__Table__Row__Cell Tables__Table__Header Tables__Table__ActionButtonsColumn Instructor__Group__Visible__ContainerTable__Table__ActionButtonsColumn'></td>
                            </tr>
                          </thead>
                        )}
                        <tbody>
                          {sortedVisibleRows.length > 0 ? (
                            sortedVisibleRows.map((row, index) => (
                              <tr key={index} className='Tables__Table__Row'>
                                <td className='Tables__Table__Row__Cell Tables__Table__InputColumn Instructor__Group__Visible__ContainerTable__Table__InputColumn'>
                                  <input
                                    type='checkbox'
                                    className='Tables__Table__InputColumn__Input'
                                    checked={isVisibleSelected(row.idZtudiante)}
                                    onChange={() =>
                                      toggleVisibleSelect(row.idZtudiante)
                                    }
                                  />
                                </td>

                                {/* Datos fijos */}
                                <td className='Tables__Table__Row__Cell Tables__Table__TextColumn Instructor__Group__Visible__ContainerTable__Table__TextColumn'>
                                  {row.name}
                                </td>
                                <td className='Tables__Table__Row__Cell Tables__Table__TextColumn Instructor__Group__Visible__ContainerTable__Table__TextColumn'>
                                  {row.lastName}
                                </td>
                                <td className='Tables__Table__Row__Cell Tables__Table__TextColumn Instructor__Group__Visible__ContainerTable__Table__TextColumn'>
                                  {row.user}
                                </td>

                                {/* Botones condicionales */}
                                <td className='Tables__Table__Row__Cell Tables__Table__ActionButtonsColumn Instructor__Group__Visible__ContainerTable__Table__ActionButtonsColumn'>
                                  {row.isAccepted ? (
                                    <button
                                      onClick={async () =>
                                        await ShowZtudentDetails(row)
                                      }
                                      className='Btn__Ztudia--PrimaryOutline'
                                    >
                                      Detalles
                                    </button>
                                  ) : (
                                    <button
                                      onClick={async () =>
                                        await ShowConfirmModal(actions.ACCEPT, row)
                                      }
                                      className='Btn__Ztudia--PrimaryOutline'
                                    >
                                      <i className='bi bi-check-circle'></i> Aceptar
                                    </button>
                                  )}
                                </td>

                                <td className='Tables__Table__Row__Cell Tables__Table__ActionButtonsColumn Instructor__Group__Visible__ContainerTable__Table__ActionButtonsColumn'>
                                  {row.isAccepted ? (
                                    <button
                                      onClick={async () =>
                                        await ShowConfirmModal(actions.HIDE, row)
                                      }
                                      className='Btn__Ztudia--PrimaryOutline'
                                    >
                                      <i className='bi bi-eye-slash'></i> Ocultar
                                    </button>
                                  ) : (
                                    <button
                                      onClick={async () =>
                                        await ShowConfirmModal(actions.REJECT, row)
                                      }
                                      className='Btn__Ztudia--PrimaryOutline'
                                    >
                                      <i className='bi bi-x-circle'></i> Rechazar
                                    </button>
                                  )}
                                </td>

                                <td className='Tables__Table__Row__Cell Tables__Table__ActionButtonsColumn Instructor__Group__Visible__ContainerTable__Table__ActionButtonsColumn'>
                                  {row.isAccepted && (
                                    <button
                                      onClick={async () =>
                                        await ShowConfirmModal(actions.RESET, row)
                                      }
                                      className='Btn__Ztudia--PrimaryOutline'
                                    >
                                      <i className='bi bi-arrow-repeat'></i> Contraseña
                                    </button>
                                  )}
                                </td>

                                <td className='Tables__Table__Row__Cell Tables__Table__ActionButtonsColumn Instructor__Group__Visible__ContainerTable__Table__ActionButtonsColumn'>
                                  {row.isAccepted && (
                                    <button
                                      onClick={async () =>
                                        await ShowConfirmModal(actions.DELETE, row)
                                      }
                                      className='Btn__Ztudia--PrimaryOutline'
                                    >
                                      <i className='bi bi-trash'></i>
                                    </button>
                                  )}
                                </td>
                              </tr>
                            ))
                          ) : (
                            <tr className='Tables__Table__Row Tables__Table__Row--Empty'>
                              <td className='Tables__Table__Row__Cell' colSpan={8}>
                                {allRows.length === 0
                                  ? 'Actualmente no hay Ztudiantes en este grupo.'
                                  : visibleSearchText
                                  ? `No hay coincidencias para “${visibleSearchText}”.`
                                  : 'Actualmente no hay Ztudiantes visibles en este grupo.'}
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </section>
                  </div>
                </>
              ) : (
                <p className='Instructor__Text--Center'>
                  No ha sido posible mostrar los Ztudiantes del grupo. Intenta
                  actualizar la página o inténtalo más tarde.
                </p>
              )}

              {/* ===================== TABLA OCULTOS ===================== */}
              {hiddenTotal > 0 &&
                (hiddenRows ? (
                  <>
                    <p className='Instructor__Title Instructor__Title--Center Instructor__SpaceUp'>
                      Ztudiantes ocultos
                    </p>
                    <p className='Instructor__Subtitle Instructor__Subtitle--Center'>
                      Total: {hiddenTotal}
                    </p>

                    {/* Buscador de ocultos */}
                    <Search
                      onChange={handleHiddenSearch}
                      extraClassName={'Instructor__SpaceUp'}
                    />

                    <div className='Instructor__SpaceUp'>
                      {/* Acciones masivas ocultos */}
                      {selectedHiddenIds.length > 0 && (
                        <div className='Tables__ExtraButtons'>
                          <button
                            className='Btn__Ztudia--PrimaryOutline Tables__ExtraButtons__Button'
                            onClick={() =>
                              ShowMultipleZtudents(
                                actions.SHOW,
                                selectedHiddenIds.map((id) =>
                                  allRows.find((row) => row.idZtudiante === id)
                                )
                              )
                            }
                          >
                            Mostrar {selectedHiddenIds.length} Ztudiante(s)
                          </button>
                          <button
                            className='Btn__Ztudia--PrimaryOutline Tables__ExtraButtons__Button'
                            onClick={() =>
                              DeleteMultipleZtudentsOfGroup(
                                actions.DELETE,
                                selectedHiddenIds.map((id) =>
                                  allRows.find((row) => row.idZtudiante === id)
                                )
                              )
                            }
                          >
                            Eliminar {selectedHiddenIds.length} Ztudiante(s)
                          </button>
                        </div>
                      )}

                      {/* Franja "X seleccionados" ocultos */}
                      {selectedHiddenIds.length > 0 && (
                        <div className='Tables__Table__RegistersSelected'>
                          <button
                            className='Tables__Table__RegistersSelected__Button Tables__Table__RegistersSelected__Button--Sm'
                            onClick={() => setSelectedHiddenIds([])}
                          >
                            <span className='Tables__Table__RegistersSelected__Button__Text'>
                              X
                            </span>
                            <span className='Tables__Table__RegistersSelected__Button__Text'>
                              Cerrar
                            </span>
                          </button>

                          <div className='Tables__Table__RegistersSelected__Text'>
                            {selectedHiddenIds.length} ztudiante(s) seleccionada(s)
                          </div>

                          <button
                            className='Tables__Table__RegistersSelected__Button Tables__Table__RegistersSelected__Button--Lg'
                            onClick={() => setSelectedHiddenIds([])}
                          >
                            <span className='Tables__Table__RegistersSelected__Button__Text'>
                              X
                            </span>
                            <span className='Tables__Table__RegistersSelected__Button__Text'>
                              Cerrar
                            </span>
                          </button>
                        </div>
                      )}

                      {/* Tabla de ocultos */}
                      <section className='Tables__Container'>
                        <table className='Tables__Table Instructor__Group__Hidden__ContainerTable__Table'>
                          {selectedHiddenIds.length === 0 && (
                            <thead>
                              <tr className='Tables__Table__Row--Header'>
                                <td className='Tables__Table__Row__Cell Tables__Table__Header Tables__Table__InputColumn Instructor__Group__Hidden__ContainerTable__Table__InputColumn'>
                                  <input
                                    type='checkbox'
                                    className='Tables__Table__InputColumn__Input'
                                    onChange={(e) => {
                                      const newIds = e.target.checked
                                        ? hiddenRows.map((row) => row.idZtudiante)
                                        : []
                                      setSelectedHiddenIds(newIds)
                                    }}
                                    checked={
                                      hiddenRows.length > 0 &&
                                      hiddenRows.every((row) =>
                                        selectedHiddenIds.includes(row.idZtudiante)
                                      )
                                    }
                                  />
                                </td>
                                <td
                                  className='Tables__Table__Row__Cell Tables__Table__Header Tables__Table__TextColumn Instructor__Group__Hidden__ContainerTable__Table__TextColumn'
                                  onClick={() => handleSortHidden('name')}
                                >
                                  Nombre(s)
                                  {sortHiddenConfig.key === 'name' && (
                                    <span>
                                      {sortHiddenConfig.direction === 'asc'
                                        ? ' ▲'
                                        : ' ▼'}
                                    </span>
                                  )}
                                </td>
                                <td
                                  className='Tables__Table__Row__Cell Tables__Table__Header Tables__Table__TextColumn Instructor__Group__Hidden__ContainerTable__Table__TextColumn'
                                  onClick={() => handleSortHidden('lastName')}
                                >
                                  Apellido(s)
                                  {sortHiddenConfig.key === 'lastName' && (
                                    <span>
                                      {sortHiddenConfig.direction === 'asc'
                                        ? ' ▲'
                                        : ' ▼'}
                                    </span>
                                  )}
                                </td>
                                <td
                                  className='Tables__Table__Row__Cell Tables__Table__Header Tables__Table__TextColumn Instructor__Group__Hidden__ContainerTable__Table__TextColumn'
                                  onClick={() => handleSortHidden('user')}
                                >
                                  Usuario
                                  {sortHiddenConfig.key === 'user' && (
                                    <span>
                                      {sortHiddenConfig.direction === 'asc'
                                        ? ' ▲'
                                        : ' ▼'}
                                    </span>
                                  )}
                                </td>
                                <td className='Tables__Table__Row__Cell Tables__Table__Header Tables__Table__ActionButtonsColumn Instructor__Group__Hidden__ContainerTable__Table__ActionButtonsColumn'></td>
                                <td className='Tables__Table__Row__Cell Tables__Table__Header Tables__Table__ActionButtonsColumn Instructor__Group__Hidden__ContainerTable__Table__ActionButtonsColumn'></td>
                                <td className='Tables__Table__Row__Cell Tables__Table__Header Tables__Table__ActionButtonsColumn Instructor__Group__Hidden__ContainerTable__Table__ActionButtonsColumn'></td>
                                <td className='Tables__Table__Row__Cell Tables__Table__Header Tables__Table__ActionButtonsColumn Instructor__Group__Hidden__ContainerTable__Table__ActionButtonsColumn'></td>
                              </tr>
                            </thead>
                          )}
                          <tbody>
                            {sortedHiddenRows.length > 0 ? (
                              sortedHiddenRows.map((row, index) => (
                                <tr key={index} className='Tables__Table__Row'>
                                  <td className='Tables__Table__Row__Cell Tables__Table__InputColumn Instructor__Group__Hidden__ContainerTable__Table__InputColumn'>
                                    <input
                                      type='checkbox'
                                      className='Tables__Table__InputColumn__Input'
                                      checked={isHiddenSelected(row.idZtudiante)}
                                      onChange={() =>
                                        toggleHiddenSelect(row.idZtudiante)
                                      }
                                    />
                                  </td>

                                  {/* Datos básicos */}
                                  <td className='Tables__Table__Row__Cell Tables__Table__TextColumn Instructor__Group__Hidden__ContainerTable__Table__TextColumn'>
                                    {row.name}
                                  </td>
                                  <td className='Tables__Table__Row__Cell Tables__Table__TextColumn Instructor__Group__Hidden__ContainerTable__Table__TextColumn'>
                                    {row.lastName}
                                  </td>
                                  <td className='Tables__Table__Row__Cell Tables__Table__TextColumn Instructor__Group__Hidden__ContainerTable__Table__TextColumn'>
                                    {row.user}
                                  </td>

                                  {/* Botones fijos */}
                                  <td className='Tables__Table__Row__Cell Tables__Table__ActionButtonsColumn Instructor__Group__Hidden__ContainerTable__Table__ActionButtonsColumn'>
                                    <button
                                      onClick={async () =>
                                        await ShowZtudentDetails(row)
                                      }
                                      className='Btn__Ztudia--PrimaryOutline'
                                    >
                                      Detalles
                                    </button>
                                  </td>

                                  <td className='Tables__Table__Row__Cell Tables__Table__ActionButtonsColumn Instructor__Group__Hidden__ContainerTable__Table__ActionButtonsColumn'>
                                    <button
                                      onClick={async () =>
                                        await ShowConfirmModal(actions.SHOW, row)
                                      }
                                      className='Btn__Ztudia--PrimaryOutline'
                                    >
                                      <i className='bi bi-eye'></i> Mostrar
                                    </button>
                                  </td>

                                  <td className='Tables__Table__Row__Cell Tables__Table__ActionButtonsColumn Instructor__Group__Hidden__ContainerTable__Table__ActionButtonsColumn'>
                                    <button
                                      onClick={async () =>
                                        await ShowConfirmModal(actions.RESET, row)
                                      }
                                      className='Btn__Ztudia--PrimaryOutline'
                                    >
                                      <i className='bi bi-arrow-repeat'></i> Contraseña
                                    </button>
                                  </td>

                                  <td className='Tables__Table__Row__Cell Tables__Table__ActionButtonsColumn Instructor__Group__Hidden__ContainerTable__Table__ActionButtonsColumn'>
                                    <button
                                      onClick={async () =>
                                        await ShowConfirmModal(actions.DELETE, row)
                                      }
                                      className='Btn__Ztudia--PrimaryOutline'
                                    >
                                      <i className='bi bi-trash'></i>
                                    </button>
                                  </td>
                                </tr>
                              ))
                            ) : (
                              <tr className='Tables__Table__Row Tables__Table__Row--Empty'>
                                <td className='Tables__Table__Row__Cell' colSpan={8}>
                                  {hiddenSearchText
                                    ? `No hay coincidencias para “${hiddenSearchText}”.`
                                    : 'No hay Ztudiantes ocultos que coincidan con el filtro.'}
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </section>
                    </div>
                  </>
                ) : (
                  <p className='Instructor__Text--Center'>
                    No ha sido posible mostrar el listado de Ztudiantes ocultos del
                    grupo. Intenta actualizar la página o inténtalo más tarde.
                  </p>
                ))}
            </>
          )}

          {/* ----------------------------------------------------------------- */}
          {/* Modal de detalles de Ztudiante                                     */}
          {/* ----------------------------------------------------------------- */}
          {showDetailsModal && (
            <Details
              stepButtons={true}
              actionToPreviousStepButton={() => PreviousStepButton()}
              showPreviousStepButton={showPreviousStepButton}
              actionToNextStepButton={() => NextStepButton()}
              showNextStepButton={showNextStepButton}
              showCloseModalButtonInContent={true}
              extraClassName={'Instructor__Group__Details'}
              setShowModal={setShowDetailsModal}
              setDetailsData={setZtudentDetails}
            >
              <div className='Instructor__Group__Details__Container'>
                <p className='Instructor__Group__Details__Title'>
                  Detalles de mis Ztudiantes
                </p>

                {ztudentDetails ? (
                  <>
                    <p className='Instructor__Group__Details__Text Instructor__Group__Details__TextContainer'>
                      Nombre(s): {ztudentDetails.name}
                    </p>
                    <p className='Instructor__Group__Details__Text Instructor__Group__Details__TextContainer'>
                      Apellido(s): {ztudentDetails.lastName}
                    </p>
                    <p className='Instructor__Group__Details__Text Instructor__Group__Details__TextContainer'>
                      Usuario: {ztudentDetails.user}
                    </p>
                    <p className='Instructor__Group__Details__Text Instructor__Group__Details__TextContainer'>
                      ¿Está oculto?: {ztudentDetails.isVisible ? 'No' : 'Sí'}
                    </p>
                    {ztudentDetails.groups &&
                      (ztudentDetails.groups.length === 0 ? (
                        <p className='Instructor__Group__Details__Text Instructor__Group__Details__TextContainer'>
                          Grupos en los que se encuentra: Ninguno
                        </p>
                      ) : (
                        <>
                          <p className='Instructor__Group__Details__Text Instructor__Group__Details__TextContainer'>
                            Grupos en los que se encuentra:
                          </p>

                          <div className='Instructor__Group__Details__ContainerTable'>
                            <table className='Instructor__Group__Details__Table'>
                              <tbody>
                                {ztudentDetails.groups.length === 1 ? (
                                  <tr className='Instructor__Group__Details__Table__Row'>
                                    <td className='Instructor__Group__Details__Table__Row__Cell'>
                                      Este mismo grupo
                                    </td>
                                    <td className='Instructor__Group__Details__Table__Row__Cell'></td>
                                  </tr>
                                ) : (
                                  ztudentDetails.groups.map((group, index) => (
                                    <tr
                                      key={index}
                                      className='Instructor__Group__Details__Table__Row'
                                    >
                                      <td className='Instructor__Group__Details__Table__Row__Cell'>
                                        {group.groupName}
                                      </td>
                                      <td className='Instructor__Group__Details__Table__Row__Cell'></td>
                                    </tr>
                                  ))
                                )}
                              </tbody>
                            </table>
                          </div>
                        </>
                      ))}
                  </>
                ) : (
                  <>
                    <p className='Instructor__Group__Details__Text'>
                      No ha sido posible mostrar la información del grupo.
                    </p>
                    <p className='Instructor__Group__Details__Text'>
                      Intenta consultar su información nuevamente o inténtalo más tarde.
                    </p>
                  </>
                )}
              </div>
            </Details>
          )}

          {/* ----------------------------------------------------------------- */}
          {/* Modal de confirmación de acciones sobre Ztudiantes                */}
          {/* ----------------------------------------------------------------- */}
          {showConfirmationModal &&
            ztudentConfirmDetails &&
            (actionTypeOfModal === actions.ACCEPT ? (
              <Confirm
                message={`¿Estás segur@ de aceptar en este grupo al Ztudiante ${ztudentConfirmDetails?.name} ${ztudentConfirmDetails?.lastName} (${ztudentConfirmDetails?.user})?`}
                actionToYesButton={AcceptZtudent}
                textYesButton={'Sí, aceptar Ztudiante'}
                textNoButton={'No'}
                setShowModal={setShowConfirmationModal}
                setConfirmData={setZtudentConfirmDetails}
              />
            ) : actionTypeOfModal === actions.REJECT ? (
              <Confirm
                message={`¿Estás segur@ de rechazar de este grupo al Ztudiante ${ztudentConfirmDetails?.name} ${ztudentConfirmDetails?.lastName} (${ztudentConfirmDetails?.user})?`}
                actionToYesButton={RejectZtudent}
                textYesButton={'Sí, rechazar Ztudiante'}
                textNoButton={'No'}
                setShowModal={setShowConfirmationModal}
                setConfirmData={setZtudentConfirmDetails}
              />
            ) : actionTypeOfModal === actions.HIDE ? (
              <Confirm
                message={`¿Estás segur@ de ocultar al Ztudiante ${ztudentConfirmDetails?.name} ${ztudentConfirmDetails?.lastName} (${ztudentConfirmDetails?.user})?`}
                actionToYesButton={HideZtudent}
                textYesButton={'Sí, ocultar Ztudiante'}
                textNoButton={'No'}
                setShowModal={setShowConfirmationModal}
                setConfirmData={setZtudentConfirmDetails}
              />
            ) : actionTypeOfModal === actions.DELETE ? (
              <Confirm
                message={`¿Estás segur@ de eliminar de este grupo al Ztudiante ${ztudentConfirmDetails?.name} ${ztudentConfirmDetails?.lastName} (${ztudentConfirmDetails?.user})?`}
                actionToYesButton={DeleteZtudentOfGroup}
                textYesButton={'Sí, eliminar Ztudiante'}
                textNoButton={'No'}
                setShowModal={setShowConfirmationModal}
                setConfirmData={setZtudentConfirmDetails}
              />
            ) : actionTypeOfModal === actions.SHOW ? (
              <Confirm
                message={`¿Estás segur@ de mostrar al Ztudiante ${ztudentConfirmDetails?.name} ${ztudentConfirmDetails?.lastName} (${ztudentConfirmDetails?.user}) que está oculto?`}
                actionToYesButton={ShowZtudent}
                textYesButton={'Sí, mostrar Ztudiante'}
                textNoButton={'No'}
                setShowModal={setShowConfirmationModal}
                setConfirmData={setZtudentConfirmDetails}
              />
            ) : (
              actionTypeOfModal === actions.RESET && (
                <Confirm
                  message={`¿Estás segur@ de resetear la contraseña del Ztudiante ${ztudentConfirmDetails?.name} ${ztudentConfirmDetails?.lastName} (${ztudentConfirmDetails?.user})?`}
                  actionToYesButton={ResetZtudiantePassword}
                  textYesButton={'Sí, resetear contraseña'}
                  textNoButton={'No'}
                  setShowModal={setShowConfirmationModal}
                  setConfirmData={setZtudentConfirmDetails}
                />
              )
            ))}

          {/* ----------------------------------------------------------------- */}
          {/* Modal de resumen de acciones masivas                              */}
          {/* ----------------------------------------------------------------- */}
          {showMassiveSummaryModal && massiveSummary && (
            <Details
              stepButtons={false}
              showCloseModalButtonInContent={true}
              extraClassName={'Modal__Layout--Summary'}
              setShowModal={setShowMassiveSummaryModal}
              setDetailsData={setMassiveSummary}
            >
              <div className='Modal__Layout--Summary__Container'>
                <p className='Modal__Layout--Summary__Title'>
                  Resumen{' '}
                  {actionTypeOfModal === actions.ACCEPT
                    ? 'de Ztudiantes aceptados'
                    : actionTypeOfModal === actions.REJECT
                    ? 'de Ztudiantes rechazados'
                    : actionTypeOfModal === actions.HIDE
                    ? 'de Ztudiantes ocultados'
                    : actionTypeOfModal === actions.DELETE
                    ? 'de Ztudiantes eliminados'
                    : actionTypeOfModal === actions.SHOW &&
                      'de Ztudiantes mostrados'}
                </p>

                {massiveSummary ? (
                  <>
                    {massiveSummary.successful.length > 0 && (
                      <>
                        <p className='Modal__Layout--Summary__Category'>
                          <i className='bi bi-check-circle'></i>{' '}
                          {actionTypeOfModal === actions.ACCEPT
                            ? 'Ztudiantes aceptados:'
                            : actionTypeOfModal === actions.REJECT
                            ? 'Ztudiantes rechazados:'
                            : actionTypeOfModal === actions.HIDE
                            ? 'Ztudiantes ocultados:'
                            : actionTypeOfModal === actions.DELETE
                            ? 'Ztudiantes eliminados:'
                            : actionTypeOfModal === actions.SHOW &&
                              'Ztudiantes mostrados:'}
                        </p>
                        <div className='Modal__Layout--Summary__TextContainer'>
                          {massiveSummary.successful.map((success, index) => (
                            <p
                              key={index}
                              className='Modal__Layout--Summary__TextContainer__Text'
                            >
                              {success.name} {success.lastName} ({success.user})
                            </p>
                          ))}
                        </div>
                      </>
                    )}

                    {massiveSummary.wrongs.length > 0 && (
                      <>
                        <p className='Modal__Layout--Summary__Category'>
                          <i className='bi bi-x-circle'></i>{' '}
                          {actionTypeOfModal === actions.ACCEPT
                            ? 'Error al aceptarlo(s):'
                            : actionTypeOfModal === actions.REJECT
                            ? 'Error al rechazarlo(s):'
                            : actionTypeOfModal === actions.HIDE
                            ? 'Error al ocultarlo(s):'
                            : actionTypeOfModal === actions.DELETE
                            ? 'Error al eliminarlo(s):'
                            : actionTypeOfModal === actions.SHOW &&
                              'Error al mostrarlo(s):'}
                        </p>
                        <div className='Modal__Layout--Summary__TextContainer'>
                          {massiveSummary.wrongs.map((wrong, index) => (
                            <p
                              key={index}
                              className='Modal__Layout--Summary__TextContainer__Text'
                            >
                              {wrong.name} {wrong.lastName} ({wrong.user})
                            </p>
                          ))}
                        </div>
                      </>
                    )}

                    {massiveSummary.omitteds.length > 0 && (
                      <>
                        <p className='Modal__Layout--Summary__Category'>
                          <i className='bi bi-skip-end-btn'></i> Omitidos:
                        </p>
                        <div className='Modal__Layout--Summary__TextContainer'>
                          {massiveSummary.omitteds.map((omitted, index) => (
                            <p
                              key={index}
                              className='Modal__Layout--Summary__TextContainer__Text'
                            >
                              {omitted.name} {omitted.lastName} ({omitted.user})
                            </p>
                          ))}
                        </div>
                      </>
                    )}
                  </>
                ) : (
                  <>
                    <p className='Modal__Layout--Summary__Text'>
                      No ha sido posible mostrar el resumen de lo realizado.
                    </p>
                  </>
                )}
              </div>
            </Details>
          )}

          {/* ----------------------------------------------------------------- */}
          {/* Modal de código de acceso                                         */}
          {/* ----------------------------------------------------------------- */}
          {showAccessCodeModal && accessCode && (
            <AccessCode accessCode={accessCode} setShowModal={setShowAccessCodeModal} />
          )}

          {/* ----------------------------------------------------------------- */}
          {/* NUEVO: Modal para editar nombre del grupo                       */}
          {/* ----------------------------------------------------------------- */}
          {showEditGroupModal && currentGroup && (
            <Details
              stepButtons={false}
              showCloseModalButtonInContent={false}
              extraClassName={'Modal__Layout--Summary'}
              setShowModal={setShowEditGroupModal}
              setDetailsData={() => {}}
            >
              <div className='Modal__Layout--Summary__Container'>
                <p className='Modal__Layout--Summary__Title'>Editar nombre del grupo</p>

                <p className='Modal__Layout--Summary__Text'>Nombre actual:</p>
                <p className='Modal__Layout--Summary__Text'>{currentGroup.name}</p>

                <label className='Form__Label'>Nuevo nombre del grupo</label>
                <input type='text' className='Form__Input Instructor__Group__Modal--Edit__Input' value={editingGroupName} onChange={(e) => setEditingGroupName(e.target.value)} />

                <div className='Modal__Layout--Summary__Buttons Instructor__Group__Modal--Edit__Buttons'>
                  <button
                    type='button'
                    className='Btn__Ztudia--PrimaryOutline Instructor__Group__Modal--Edit__Buttons__Button'
                    disabled={
                      !editingGroupName.trim() ||
                      editingGroupName.trim() === currentGroup.name
                    }
                    onClick={() => {
                      const newName = editingGroupName.trim()
                      if (!newName || newName === currentGroup.name) return

                      // Cerramos modal de edición
                      setShowEditGroupModal(false)

                      // Guardamos datos para el modal de confirmación
                      setGroupRenameConfirmData({
                        //idGroup: currentGroup.idGroup,
                        oldName: currentGroup.name,
                        newName: newName
                      })

                      // Mostramos modal de confirmación de renombrado
                      setShowGroupRenameConfirmModal(true)
                    }}
                  >
                    Guardar cambios
                  </button>

                  <button type='button' className='Btn__Ztudia--DangerOutline Instructor__Group__Modal--Edit__Buttons__Button' onClick={() => setShowEditGroupModal(false)}>Cancelar</button>
                </div>
              </div>
            </Details>
          )}

          {/* ----------------------------------------------------------------- */}
          {/* Modal de confirmación para renombrar grupo */}
          {/* ----------------------------------------------------------------- */}
          {showGroupRenameConfirmModal && groupRenameConfirmData && (
            <Confirm
              message={`¿Estás segur@ de cambiar el nombre del grupo "${groupRenameConfirmData.oldName}" a "${groupRenameConfirmData.newName}"?`}
              actionToYesButton={RenameCurrentGroup}
              textYesButton={'Sí, cambiar nombre del grupo'}
              textNoButton={'No'}
              setShowModal={setShowGroupRenameConfirmModal}
              setConfirmData={setGroupRenameConfirmData}
            />
          )}
        </section>
      </InstructorBase>
    </>
  )
}

export { Group };